import { isContactAfterpaySupportFlow } from './disputeFlow'
import React, { FC, ReactNode, useCallback, useContext, useEffect, useRef } from 'react'

import { DisputeCreationModalContext, DisputeCreationModalProvider, DisputeCreationModalSteps } from './DisputeCreationModalContext'
import { Modal } from '@afterpaytouch/core'
import { OrderResponse } from '@afterpaytouch/portal-api'

import { SelectReason } from './SelectReason'
import { ContactMerchant } from './ContactMerchant'
import { SubmitEvidence } from './SubmitEvidence'
import { CreateFailure, CreateSuccess } from './Result'
import { Alibaba } from './Alibaba'
import { Decline } from './Decline'
import { ContactMerchantInfo } from './ContactMerchantInfo'
import { DisagreeWithDeliveryWarn } from './DisagreeWithDeliveryWarn'
import { NoPermission } from './NoPermission'
import { Disclaimer } from './Disclaimer/Disclaimer'
import { TimeLimitTip } from './TimeLimitTip'
import { Prezzee } from './Prezzee'
import { ContactAfterpaySupport, useContactSupportTracking } from './ContactAfterpaySupport'
import { TrackingEvent } from '../../model/amplitude'

export interface DisputeCreationModalProps {
  isModalOpen: boolean
  closeModal: () => void
  order: OrderResponse
  refreshDisputeInfo: () => void
  animate: boolean
  reopenGetHelpModal: () => void
  initialStep?: DisputeCreationModalSteps
}

export const DisputeCreationSteps: FC<Omit<DisputeCreationModalProps, 'order' | 'refreshDisputeInfo'>> = ({ isModalOpen, animate, reopenGetHelpModal }) => {
  const {
    step,
    closeModal: closeContextModal,
    isLoading,
    result: { success, error },
    resetState,
  } = useContext(DisputeCreationModalContext)

  const renderStep: () => ReactNode = useCallback(() => {
    if (step === DisputeCreationModalSteps.ContactAfterpaySupport) return <ContactAfterpaySupport reopenGetHelpModal={reopenGetHelpModal} />
    if (success) {
      return <CreateSuccess />
    }

    if (error) {
      return <CreateFailure />
    }
    switch (step) {
      case DisputeCreationModalSteps.SelectReason:
        return <SelectReason />
      case DisputeCreationModalSteps.ContactMerchant:
        return <ContactMerchant reopenGetHelpModal={reopenGetHelpModal} />
      case DisputeCreationModalSteps.ContactMerchantInfo:
        return <ContactMerchantInfo />
      case DisputeCreationModalSteps.SubmitEvidence:
        return <SubmitEvidence />
      case DisputeCreationModalSteps.Alibaba:
        return <Alibaba />
      case DisputeCreationModalSteps.Decline:
        return <Decline />
      case DisputeCreationModalSteps.DisagreeWithDeliveryWarn:
        return <DisagreeWithDeliveryWarn />
      case DisputeCreationModalSteps.NoPermission:
        return <NoPermission />
      case DisputeCreationModalSteps.Disclaimer:
        return <Disclaimer />
      case DisputeCreationModalSteps.TimeLimitTip:
        return <TimeLimitTip />
      case DisputeCreationModalSteps.Prezzee:
        return <Prezzee />
    }
  }, [success, error, step, reopenGetHelpModal])

  useEffect(() => {
    return () => {
      resetState()
    }
  }, [])

  return (
    <Modal
      show={isModalOpen}
      onClose={closeContextModal}
      onBackdropClick={closeContextModal}
      onHide={closeContextModal}
      onEscapeKeyDown={closeContextModal}
      testNameSpace='dispute-creation-modal'
      showCloseButton={
        ![DisputeCreationModalSteps.ContactMerchant, DisputeCreationModalSteps.SubmitEvidence, DisputeCreationModalSteps.ContactAfterpaySupport].includes(
          step
        ) ||
        (step !== DisputeCreationModalSteps.ContactAfterpaySupport && (success || Boolean(error)))
      }
      loading={isLoading}
      animate={animate}
    >
      {renderStep()}
    </Modal>
  )
}

export const DisputeCreationModal: FC<DisputeCreationModalProps> = ({
  isModalOpen,
  closeModal,
  order,
  refreshDisputeInfo,
  animate,
  reopenGetHelpModal,
  initialStep,
}) => {
  const track = useContactSupportTracking()
  const closed = useRef(false)
  useEffect(() => {
    if (isModalOpen) closed.current = false
  }, [isModalOpen])
  const handleClose = () => {
    if (!closed.current && isContactAfterpaySupportFlow(order?.disputeFlowType)) {
      closed.current = true
      track(TrackingEvent.PRESSED_CREATE_DISPUTE_CANCEL_CONTACT_AFTERPAY_SUPPORT)
    }
    closeModal()
  }
  return isModalOpen ? (
    <DisputeCreationModalProvider closeModal={handleClose} order={order} refreshDisputeInfo={refreshDisputeInfo} initialStep={initialStep}>
      <DisputeCreationSteps isModalOpen={isModalOpen} closeModal={closeModal} animate={animate} reopenGetHelpModal={reopenGetHelpModal} />
    </DisputeCreationModalProvider>
  ) : null
}
